/**
 * Frame arithmetic, matching the player exactly (DEV-654).
 *
 * The editor shows no numeric playback position at all today — the timeline
 * header carries only the *total* duration, the playhead is an unlabelled div,
 * and the player's own OSD is deliberately hidden in the editor
 * (`[hideOsdTimeline]="true"`). The frame is the number that matters here:
 * `ContentItem.startFrame`, `Placement.frames`, `OpacityKeyframe`,
 * `SceneEffect.frameIndex` and `SceneData.framesCount` are all frame-indexed,
 * so an operator lining up an overlay needs a frame, not a wall-clock second.
 *
 * ## Why this formula and not the obvious one
 *
 * `@webout-spark/player@1.6.214` does, de-minified:
 *
 * ```js
 * this.currentFrameIndex = Math.floor(this.currentTime / (1000 / this.fps) + 1e-6);
 * ```
 *
 * `Math.round(ms / 1000 * fps)` is off by one for most of every frame's
 * duration — it reports the *nearest* frame boundary, while the player reports
 * the frame currently on screen. The `1e-6` epsilon is what stops float drift
 * from showing 47 where the player renders 48: at 25 fps, frame 48 starts at
 * 1920 ms, and `1920 / 40` can land a hair under 48.
 *
 * A readout that disagrees with the player by one frame is worse than no
 * readout, because the numbers it is compared against — the ones typed into
 * scene forms — are the player's.
 *
 * ## Chapter-local, never project-wide
 *
 * `currentTime` resets to 0 at the top of every chapter and is clamped to that
 * chapter's duration, so an index derived from it is a **chapter** frame. That
 * matches the timeline, which renders one chapter at a time. A whole-project
 * frame is not derivable from the player at all — it would have to be summed
 * over preceding chapters on our side and would then disagree with anything the
 * player reports.
 */

/** Fallback fps, mirroring `timeline-panel.ts`'s own `projectData()?.fps ?? 25`. */
const DEFAULT_FPS = 25;

function safeFps(fps: number | undefined | null): number {
	return typeof fps === 'number' && Number.isFinite(fps) && fps > 0 ? fps : DEFAULT_FPS;
}

/** The frame on screen at `ms`, by the player's own formula. Never negative. */
export function frameFromMs(ms: number, fps: number): number {
	if (!Number.isFinite(ms) || ms <= 0) return 0;
	return Math.floor(ms / (1000 / safeFps(fps)) + 1e-6);
}

/** The instant a frame begins. Inverse of {@link frameFromMs} at frame boundaries. */
export function msFromFrame(frame: number, fps: number): number {
	if (!Number.isFinite(frame) || frame <= 0) return 0;
	return (frame * 1000) / safeFps(fps);
}

/**
 * `m:ss.SS` — minutes, seconds, hundredths.
 *
 * Hundredths rather than milliseconds: three digits jitter distractingly during
 * playback and nothing in the data model is addressed in milliseconds.
 */
export function formatTimecode(ms: number): string {
	const total = Number.isFinite(ms) && ms > 0 ? ms : 0;
	const minutes = Math.floor(total / 60000);
	const seconds = Math.floor((total % 60000) / 1000);
	const hundredths = Math.floor((total % 1000) / 10);
	return `${minutes}:${String(seconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
}

/** The block under the playhead, in the shape the timeline already builds. */
export interface FrameBlock {
	slug: string;
	startSeconds: number;
	durationSeconds: number;
	framesCount: number;
}

export interface PlayheadPosition {
	/** Frame within the chapter — the raw number the player itself holds. */
	chapterFrame: number;
	/** The block under the playhead, or `null` past the last one. */
	block: FrameBlock | null;
	/** Frame within that block, counted from 0. `null` when there is no block. */
	sceneFrame: number | null;
}

/**
 * Resolve the playhead into a chapter frame and a scene-local one.
 *
 * Split scenes need no special case: `fetchSceneBlocks` emits one block per
 * part, each with its own `framesCount`, and each part is its own scene
 * document whose `startFrame`/`frames` fields already count from 0. Subtracting
 * the block's own start is therefore exactly the number shown in the scene
 * form.
 *
 * The scene frame is derived from frames, not from seconds: converting the
 * block's start to a frame and subtracting keeps the result consistent with the
 * chapter frame at every boundary, where `startSeconds * fps` can sit a hair
 * either side of an integer.
 */
export function resolvePlayhead(ms: number, fps: number, blocks: readonly FrameBlock[]): PlayheadPosition {
	const rate = safeFps(fps);
	const chapterFrame = frameFromMs(ms, rate);
	const seconds = Math.max(ms, 0) / 1000;

	// The last block that has started, so a playhead exactly on a boundary
	// belongs to the block beginning there — the same convention the player's
	// floor uses for frames.
	let found: FrameBlock | null = null;
	for (const block of blocks) {
		if (seconds + 1e-9 >= block.startSeconds) found = block;
		else break;
	}
	// Past the end of the last block, there is nothing under the playhead.
	if (found && seconds >= found.startSeconds + found.durationSeconds) found = null;
	if (!found) return { chapterFrame, block: null, sceneFrame: null };

	const blockStartFrame = frameFromMs(found.startSeconds * 1000, rate);
	return {
		chapterFrame,
		block: found,
		sceneFrame: Math.max(chapterFrame - blockStartFrame, 0),
	};
}
